import { useState } from "react";
import { Copy, QrCode } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/ui/button";
import { referralQrSrc, type ReferralLink } from "@/features/affiliates/referral-link";
import { HintNote } from "@/components/HintTip";

/**
 * Link giới thiệu của nhân viên, ngay dưới ô mã (CEO chốt 1a · 2a, 01/10/2026).
 * Link và ảnh mã QR đều do AFF phát — ở đây chỉ HIỆN, không tự ghép link từ mã.
 * Mã QR chỉ mở khi bấm, để hộp không dài thêm với người không cần.
 */
export function MyAffiliateLink({ link }: { link: ReferralLink }) {
  return (
    <section
      aria-label="Link giới thiệu"
      className="rounded-md border border-border bg-surface px-4 py-3"
    >
      <div className="text-sm text-muted-foreground">Link giới thiệu</div>
      <div className="break-all text-sm font-medium">{link.url}</div>
      <HintNote className="mt-1">Khách đặt lịch qua link này thì đơn tự ghi mã của bạn.</HintNote>
      <ReferralQr link={link} alt="Mã QR của link giới thiệu" />
    </section>
  );
}

/**
 * Một link sản phẩm đã chọn (ô «Chọn sản phẩm», bản 2 — CEO 1a 30/10/2026):
 * tên món, link, nút Copy và mã QR riêng. Nút "Copy link" ở chân hộp vẫn chỉ
 * copy link chung.
 */
export function ProductLinkCard({ link, name }: { link: ReferralLink; name: string }) {
  const copy = async () => {
    await navigator.clipboard.writeText(link.url);
    toast.success(`Đã copy link ${name}`);
  };
  return (
    <div className="mt-3 border-t border-dashed border-border pt-3">
      <div className="text-sm font-semibold">{name}</div>
      <div className="break-all text-sm text-muted-foreground">{link.url}</div>
      <div className="mt-1.5 flex flex-wrap items-start gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={`Copy link ${name}`}
          onClick={() => void copy()}
        >
          <Copy className="h-4 w-4" />
          Copy
        </Button>
        <ReferralQr link={link} alt={`Mã QR của link ${name}`} toggleLabel={name} inline />
      </div>
    </div>
  );
}

/** Nút mở / đóng mã QR AFF gửi kèm link; không có ảnh hợp lệ thì không có nút. */
function ReferralQr({
  link,
  alt,
  toggleLabel,
  inline = false,
}: {
  link: ReferralLink;
  alt: string;
  toggleLabel?: string;
  inline?: boolean;
}) {
  const [showQr, setShowQr] = useState(false);
  const qrSrc = referralQrSrc(link);
  if (!qrSrc) return null;
  const text = showQr ? "Ẩn mã QR" : "Hiện mã QR";

  return (
    <div className={inline ? "contents" : undefined}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className={inline ? undefined : "mt-2"}
        aria-expanded={showQr}
        aria-label={toggleLabel ? `${text} ${toggleLabel}` : undefined}
        onClick={() => setShowQr((open) => !open)}
      >
        <QrCode className="h-4 w-4" />
        {text}
      </Button>
      {showQr ? (
        <figure className="mt-3 flex w-full flex-col items-center gap-2">
          <img
            src={qrSrc}
            alt={alt}
            width={208}
            height={208}
            className="h-52 w-52 rounded-md border border-border bg-white p-2 [image-rendering:pixelated]"
          />
          <figcaption className="text-center text-xs text-muted-foreground">
            Đưa khách quét mã này để mở link.
          </figcaption>
        </figure>
      ) : null}
    </div>
  );
}
